/**
 * @fileoverview Generic type-to-filter combobox.
 * @description A dropdown that behaves like a `<select>` but adds a search box once open — a plain
 * `<select>` is fine for a handful of options, but stops being usable once a list grows past a
 * screenful (e.g. 40+ bank branches), since finding one by scrolling/first-letter-jump gets slow.
 * Typing narrows the list instantly instead. Ported from the InsightBox Banking design system's
 * own `SearchableSelect`, generalized (icons inlined to match this app's own icon convention
 * instead of `lucide-react`) since any page with a long option list can reuse it, not just a
 * branch filter.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect, useMemo, useRef, useState } from 'react'

export interface SearchableSelectOption {
  value: string
  label: string
}

interface SearchableSelectProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: SearchableSelectOption[]
  disabled?: boolean
  dense?: boolean
  placeholder: string
  searchPlaceholder: string
  noMatchesLabel: string
}

/**
 * @component
 * @param {SearchableSelectProps} props - The field's label, current value, options, and copy. Copy
 * props are required (not defaulted) rather than hardcoded English fallbacks, so every call site
 * is forced to supply already-translated text per this repo's i18n convention.
 * @param {string} props.label - The small label above the control.
 * @param {string} props.value - The currently-selected option's value.
 * @param {(value: string) => void} props.onChange - Called with the newly-selected value.
 * @param {SearchableSelectOption[]} props.options - The full, unfiltered option list.
 * @param {boolean} [props.disabled] - Disables the control. Off by default.
 * @param {boolean} [props.dense] - Tighter vertical padding on the trigger button. Off by default.
 * @param {string} props.placeholder - Trigger label shown when nothing is selected.
 * @param {string} props.searchPlaceholder - The search box's placeholder.
 * @param {string} props.noMatchesLabel - Shown when the search matches nothing.
 */
function SearchableSelect({
  label,
  value,
  onChange,
  options,
  disabled,
  dense = false,
  placeholder,
  searchPlaceholder,
  noMatchesLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [alignRight, setAlignRight] = useState(false)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const current = options.find((option) => option.value === value)

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) {
      return options
    }
    return options.filter((option) => option.label.toLowerCase().includes(normalizedQuery))
  }, [options, query])

  useEffect(() => {
    /**
     * @description Closes the dropdown when a click lands outside it.
     * @param {MouseEvent} event - The document mousedown event.
     */
    function handleOutsideClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  useEffect(() => {
    if (open) {
      // Autofocus the search box on open — typing immediately is the whole point of this control.
      requestAnimationFrame(() => inputRef.current?.focus())

      // The panel is `w-64` (256px) and left-anchored by default. For a trigger sitting near the
      // right edge of the viewport (e.g. the last filter in a right-aligned header row), that
      // would run the panel off-screen — clipped by an ancestor's scroll container rather than
      // reflowing the page, so it reads as the dropdown silently cutting off instead of erroring.
      // Anchoring to the trigger's right edge instead (opening leftward) keeps it fully visible.
      const PANEL_WIDTH = 256
      const VIEWPORT_MARGIN = 16
      const triggerRect = ref.current?.getBoundingClientRect()
      setAlignRight(
        triggerRect !== undefined &&
          triggerRect.left + PANEL_WIDTH + VIEWPORT_MARGIN > window.innerWidth,
      )
    }
  }, [open])

  /**
   * @description Commits a selection and closes the dropdown.
   * @param {string} nextValue - The chosen option's value.
   */
  function select(nextValue: string) {
    onChange(nextValue)
    setOpen(false)
    setQuery('')
  }

  /**
   * @description Handles Escape/ArrowUp/ArrowDown/Enter within the search box.
   * @param {React.KeyboardEvent} event - The search input's keydown event.
   */
  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      setOpen(false)
      setQuery('')
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlighted((index) => Math.min(index + 1, filtered.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = filtered[highlighted]
      if (option) {
        select(option.value)
      }
    }
  }

  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-label">{label}</span>
      <div className="relative" ref={ref}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (!open) {
              setHighlighted(0)
            }
            setOpen((isOpen) => !isOpen)
          }}
          className={`flex min-w-44 items-center justify-between gap-2 rounded-lg border border-border bg-white pr-2.5 pl-3 text-left text-sm font-medium text-text shadow-sm outline-none transition focus:border-primary disabled:cursor-not-allowed disabled:bg-input-bg disabled:text-label ${dense ? 'py-1.5' : 'py-2'}`}
        >
          <span className="truncate">{current?.label ?? placeholder}</span>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            className={`shrink-0 text-label transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          >
            <path d="M6 9.5l6 6 6-6" />
          </svg>
        </button>

        {open && (
          <div
            className={`absolute z-50 mt-1 w-64 overflow-hidden rounded-xl border border-border bg-white shadow-card ${alignRight ? 'right-0' : 'left-0'}`}
          >
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="shrink-0 text-label"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="6.5" />
                <path d="M16 16l4.5 4.5" />
              </svg>
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setHighlighted(0)
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="w-full bg-transparent text-sm text-text outline-none placeholder:text-label"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-3 text-sm text-label">{noMatchesLabel}</p>
              ) : (
                filtered.map((option, index) => (
                  <button
                    key={option.value}
                    type="button"
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => select(option.value)}
                    className={`flex w-full items-center px-3 py-2 text-left text-sm transition ${
                      option.value === value ? 'font-semibold text-primary' : 'text-text'
                    } ${index === highlighted ? 'bg-primary-light/50' : 'hover:bg-input-bg'}`}
                  >
                    {option.label}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </label>
  )
}

export default SearchableSelect
